import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Eye, ArrowUp, Vote, Users } from 'lucide-react';
import api from '../../lib/api';
import {
  getFilteredCommittees,
  formatCommitteeDisplayName,
} from '../../hooks/useBillFilters';
import Chip from '../Chip';
import ActionsHeaderHelp from '../ActionsHeaderHelp';
import SponsorDisplay from '../SponsorDisplay';
import SearchField from '../SearchField';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../StandardTable';
import { LOBBYING_API_TIMEOUT_MS } from './constants';
import ViewToggle from '../ViewToggle';
import FilterActions from '../FilterActions';
import MultiSelectDropdown, { type MultiSelectOption } from '../MultiSelectDropdown';

const BILL_STATUS_OPTIONS: MultiSelectOption[] = [
  { value: 'introduced', label: 'Introduced' },
  { value: 'passed', label: 'Passed' },
  { value: 'failed', label: 'Failed' },
  { value: 'signed', label: 'Signed' },
];

/** Past this many options a bare checkbox list is harder to scan than the native select it replaced. */
const SEARCHABLE_OPTION_THRESHOLD = 15;

// Sponsor interface matching SponsorDisplay component
interface BillSponsor {
  name: string;
  title: string;
  type?: string;
  sponsor_type?: string;
  party?: string;
  legislator_id?: string;
}

// Types - Using the same Bill interface as LegislationPage
interface Bill {
  id: string;
  bill_id: string;
  billNumber: string;
  title: string;
  summary?: string;
  bill_type: string;
  status: string;
  latest_action?: string;
  latest_action_date?: string;
  session_year: string;
  chamber: string;
  fiscal_impact?: string;
  sponsors: BillSponsor[];
  subject: string[];
  categories: string[];
  committees: Array<{ name: string; chamber: string }>;
  lobbyistCount: number;
  clientCount: number;
  created_at: string;
  updated_at: string;
}

interface BillsResponse {
  bills: Bill[];
  total: number;
}

interface FilterOptions {
  subjects: string[];
  sponsors: string[];
  committees: { id: string | number; name: string; chamber: string }[];
}

type SortField = 'billNumber' | 'title' | 'subject' | 'status' | 'lastActivity' | 'lobbyistCount' | 'clientCount';
type SortDirection = 'asc' | 'desc';

const BillsTab: React.FC = () => {
  const navigate = useNavigate();
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table');
  const [totalBillsCount, setTotalBillsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Multi-select filters are arrays; an empty array means "no filter".
  const [filters, setFilters] = useState({
    chamber: 'All chambers',
    subject: [] as string[],
    status: [] as string[],
    sponsor: [] as string[],
    committee_id: [] as string[],
    committee: '',
  });

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    subjects: [],
    sponsors: [],
    committees: []
  });

  // Sorting state
  const [sortBy, setSortBy] = useState<SortField>('billNumber');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Scroll to top state
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Fetch bills data - same as LegislationPage
  const fetchBills = useCallback(async (page: number = 1, append: boolean = false) => {
    try {
      if (!append) {
        setLoading(true);
        setCurrentPage(page);
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams({
        sortBy: sortBy === 'billNumber' ? 'number' : sortBy,
        sortDirection: sortDirection,
        page: page.toString(),
        limit: '20', // Load 20 bills per page
        ...(searchQuery && { search: searchQuery }),
        ...(filters.chamber !== 'All chambers' && { chamber: filters.chamber }),
        ...(filters.committee &&
          filters.committee_id.length === 0 && { committee: filters.committee }),
      });

      /*
        Multi-select filters go out as repeated params (?subject=a&subject=b),
        which the object-literal form above cannot express. Not comma-joined:
        subject and committee names contain commas.
      */
      const appendMulti = (key: string, values: string[]) => {
        for (const value of values) params.append(key, value);
      };
      appendMulti('subject', filters.subject);
      appendMulti('status', filters.status);
      appendMulti('sponsor', filters.sponsor);
      appendMulti('committee_id', filters.committee_id.filter((id) => /^\d+$/.test(id)));


      const response = await api.get(`/bills?${params}`, {
        timeout: LOBBYING_API_TIMEOUT_MS,
      });
      
      if (response.data.success) {
        const data: BillsResponse = response.data.data;
        
        if (append) {
          setBills(prev => {
            // Deduplicate by bill ID to prevent duplicate keys
            const existingIds = new Set(prev.map(b => b.id));
            const newBills = data.bills.filter(bill => !existingIds.has(bill.id));
            const combined = [...prev, ...newBills];
            
            // For count-based sorting, don't re-sort since backend handles it
            if (sortBy === 'lobbyistCount' || sortBy === 'clientCount') {
              return combined;
            }
            
            // Re-sort the combined array for other fields
            return combined.sort((a, b) => {
              // Handle other fields as needed
              if (sortBy === 'billNumber') {
                const aVal = a.billNumber || '';
                const bVal = b.billNumber || '';
                return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
              }
              
              if (sortBy === 'status') {
                const aVal = a.status || '';
                const bVal = b.status || '';
                return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
              }
              
              // For other fields, just maintain original order
              return 0;
            });
          });
          setCurrentPage(page); // FIXED: Update currentPage for infinite scroll
        } else {
          setBills(data.bills);
          setCurrentPage(page);
        }
        
        setTotalBillsCount(data.total || data.bills.length);
        
        // Check if there are more bills to load
        const itemsPerPage = 20;
        const totalPages = Math.ceil((data.total || 0) / itemsPerPage);
        setHasMore(page < totalPages);
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
      if (!append) {
        setBills([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery, filters, sortBy, sortDirection]);

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/bills/filters/options');
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Handle sorting
  const handleSort = (field: string) => {
    const sortField = field as SortField;
    if (sortBy === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(sortField);
      setSortDirection('asc');
    }
  };

  // Navigate to bill detail page with lobbyists tab active
  const handleBillClick = (billId: string) => {
    navigate(`/legislation/${billId}?tab=lobbyists`);
  };

  // Scroll to top functionality
  const scrollToTop = () => {
    // Scroll both window and main container to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Also scroll the main container if it exists and is scrolled
    const mainContainers = document.querySelectorAll('main.overflow-y-auto');
    for (const container of mainContainers) {
      if (container.scrollHeight > container.clientHeight && container.clientHeight > 0 && container.scrollTop > 0) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      chamber: 'All chambers',
      subject: [],
      status: [],
      sponsor: [],
      committee_id: [],
      committee: '',
    });
    setSearchQuery('');
  };

  // Load more bills function
  const loadMoreBills = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchBills(currentPage + 1, true);
    }
  }, [currentPage, loadingMore, hasMore, fetchBills]);

  // Initial data fetch
  useEffect(() => {
    fetchBills(1, false);
    fetchFilterOptions();
  }, [fetchBills]);

  // Refetch when filters change (reset to page 1)
  useEffect(() => {
    // Always debounce to avoid race conditions
    const timeoutId = setTimeout(() => {
      fetchBills(1, false);
    }, 100); // Reduced debounce for better responsiveness

    return () => clearTimeout(timeoutId);
  }, [fetchBills]);

  // Handle scroll event to show/hide scroll to top button and infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      // Check both window scroll and main container scroll
      const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Find the main scrollable container (the one with actual content)
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      let containerScrollTop = 0;
      let mainContainer = null;
      
      // Find the main container that actually has scrollable content
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          containerScrollTop = Math.max(containerScrollTop, container.scrollTop);
          mainContainer = container;
        }
      }
      
      const totalScroll = Math.max(windowScrollTop, containerScrollTop);
      setShowScrollToTop(totalScroll > 300); // Show button after scrolling 300px

      // Infinite scroll detection
      if (hasMore && !loadingMore) {
        let scrollThreshold;
        
        if (mainContainer) {
          // Check if we're near the bottom of the main container
          const containerBottom = mainContainer.scrollHeight - mainContainer.scrollTop - mainContainer.clientHeight;
          scrollThreshold = containerBottom < 200; // Trigger when 200px from bottom
        } else {
          // Fallback to window scroll detection
          const windowBottom = document.documentElement.scrollHeight - window.innerHeight - windowScrollTop;
          scrollThreshold = windowBottom < 200;
        }
        
        if (scrollThreshold) {
          fetchBills(currentPage + 1, true);
        }
      }
    };

    const setupScrollListeners = () => {
      // Add listeners to both window and main container
      window.addEventListener('scroll', handleScroll);
      
      // Find the main container that actually has scrollable content
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          container.addEventListener('scroll', handleScroll);
          return container;
        }
      }
      return null;
    };

    // Try to setup listeners immediately
    let mainContainer = setupScrollListeners();

    // If main container wasn't ready, try again after bills load
    let retryTimer: number | null = null;
    if (!mainContainer && bills.length === 0) {
      retryTimer = window.setTimeout(() => {
        mainContainer = setupScrollListeners();
      }, 1000);
    }

    // Force show button if page is tall and has many bills
    if (bills.length >= 50) {
      const checkPageHeight = () => {
        const bodyHeight = document.body.scrollHeight;
        const windowHeight = window.innerHeight;
        if (bodyHeight > windowHeight * 2) {
          handleScroll();
        }
      };
      setTimeout(checkPageHeight, 100);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (mainContainer) {
        mainContainer.removeEventListener('scroll', handleScroll);
      }
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
      }
    };
  }, [bills, hasMore, loadingMore, fetchBills, currentPage, sortBy, sortDirection]); // Re-run when bills change or infinite scroll state changes



  type BillChipVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'primary' | 'accent';

  const getBillStatusChipVariant = (status: string): BillChipVariant => {
    switch (status?.toLowerCase()) {
      case 'passed':
      case 'signed':
        return 'success';
      case 'failed':
      case 'defeated':
        return 'danger';
      case 'pending':
      case 'introduced':
        return 'info';
      case 'amended':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const CATEGORY_CHIP_VARIANTS: BillChipVariant[] = [
    'success',
    'info',
    'primary',
    'accent',
    'warning',
  ];

  const getCategoryChipVariant = (category: string): BillChipVariant => {
    const index =
      Math.abs(category.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) %
      CATEGORY_CHIP_VARIANTS.length;
    return CATEGORY_CHIP_VARIANTS[index];
  };

  const renderCard = (bill: Bill) => (
    <div
      key={`${bill.id}-${bill.billNumber}`}
      className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer"
      onClick={() => handleBillClick(bill.id)}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-1">
            {bill.billNumber}
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-2 line-clamp-2">
            {bill.title}
          </p>
        </div>
        <Chip 
          label={bill.status || 'Unknown'}
          variant={getBillStatusChipVariant(bill.status)}
          size="sm"
        />
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
          <span className="font-medium mr-2">Chamber:</span>
          {bill.chamber}
        </div>

          <div className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
          <span className="font-medium mr-2">Type:</span>
          {bill.bill_type}
        </div>

        {bill.latest_action && (
        <div className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
            <span className="font-medium mr-2">Latest Action:</span>
            <span className="truncate">{bill.latest_action}</span>
        </div>
        )}
      </div>

      <div className="pt-4 border-t border-border-light dark:border-border-dark">
        <button 
          onClick={() => handleBillClick(bill.id)}
          className="w-full flex items-center justify-center gap-2 text-sm text-text-tertiary dark:text-text-dark-tertiary hover:text-text-secondary dark:hover:text-text-dark-secondary font-medium"
        >
          <Eye className="w-4 h-4" />
          View Details
        </button>
      </div>
    </div>
  );

  const renderTable = () => (
    <StandardTable>
      <StandardTableHeader>
        <StandardTableRow>
          <StandardTableHeaderCell
            sortable
            sortKey="billNumber"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnType="auto"
          >
            Bill
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            columnType="expand"
            responsiveHide="md"
          >
            Subjects
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            sortable
            sortKey="status"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnType="fit"
            responsiveHide="md"
          >
            Status
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            columnType="expand"
            responsiveHide="xl"
          >
            Sponsors
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            sortable
            sortKey="lobbyistCount"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnType="fit"
            responsiveHide="lg"
          >
            Lobbyists
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            sortable
            sortKey="clientCount"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnType="fit"
            responsiveHide="lg"
          >
            Clients
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            columnType="actions"
            align="right"
          >
            <span className="inline-flex items-center gap-1.5">
              Actions
              <ActionsHeaderHelp icons={['view', 'votes', 'lobbyist-filings']} />
            </span>
          </StandardTableHeaderCell>
        </StandardTableRow>
      </StandardTableHeader>
      <StandardTableBody>
        {bills && bills.map((bill) => (
          <StandardTableRow 
            key={`bill-${bill.id}-${bill.billNumber}`}
            onClick={() => handleBillClick(bill.id)}
          >
            <StandardTableCell>
              <div>
                <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  {bill.billNumber || 'No Number'}
                </p>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary line-clamp-2">
                  {bill.title || 'No Title'}
                </p>
                {/* Show subjects on mobile as chips */}
                <div className="flex flex-wrap gap-1 mt-1 md:hidden">
                  {bill.categories && bill.categories.slice(0, 2).map((categoryName, index) => (
                    <Chip
                      key={index}
                      label={categoryName}
                      variant={getCategoryChipVariant(categoryName)}
                      size="xs"
                    />
                  ))}
                  {bill.categories && bill.categories.length > 2 && (
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                      +{bill.categories.length - 2} more
                    </span>
                  )}
                </div>
              </div>
            </StandardTableCell>
            
            <StandardTableCell responsiveHide="md">
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary line-clamp-2">
                {bill.categories && bill.categories.length > 0 
                  ? bill.categories.join(', ')
                  : 'No subjects'
                }
              </p>
            </StandardTableCell>
            
            <StandardTableCell responsiveHide="md">
              <span className="text-sm text-text-primary dark:text-text-dark-primary">
                {bill.status.length > 20 ? `${bill.status.substring(0, 20)}...` : bill.status}
              </span>
            </StandardTableCell>
            
            <StandardTableCell responsiveHide="xl">
              <div className="text-xs text-text-secondary dark:text-text-dark-secondary line-clamp-2">
                <SponsorDisplay sponsors={bill.sponsors} billNumber={bill.billNumber} />
              </div>
            </StandardTableCell>
            
            <StandardTableCell responsiveHide="lg">
              <span className="text-sm text-text-primary dark:text-text-dark-primary">
                {bill.lobbyistCount || 0}
              </span>
            </StandardTableCell>
            
            <StandardTableCell responsiveHide="lg">
              <span className="text-sm text-text-primary dark:text-text-dark-primary">
                {bill.clientCount || 0}
              </span>
            </StandardTableCell>
            
            <StandardTableCell align="right">
              <div className="flex items-center gap-1 sm:gap-2">
                <button 
                  className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBillClick(bill.id);
                  }}
                  title="View details"
                  aria-label="View details"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button 
                  className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-purple-600 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    const url = `/legislation/${bill.id}?tab=votes&returnTo=lobbying-bills`;
                    navigate(url, { 
                      state: { returnTo: 'lobbying-bills', returnTab: 'bills' }
                    });
                  }}
                  title="View votes"
                  aria-label="View votes"
                >
                  <Vote className="w-4 h-4" />
                </button>
                <button 
                  className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    const url = `/legislation/${bill.id}?tab=lobbyists&returnTo=lobbying-bills`;
                    navigate(url, { 
                      state: { returnTo: 'lobbying-bills', returnTab: 'bills' }
                    });
                  }}
                  title="View lobbyist filings"
                  aria-label="View lobbyist filings"
                >
                  <Users className="w-4 h-4" />
                </button>
              </div>
            </StandardTableCell>
          </StandardTableRow>
        ))}
      </StandardTableBody>
    </StandardTable>
  );

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="card">
        <div className="space-y-4">
          {/* Search Bar */}
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search bills by number, title, or keywords..."
            className="w-full"
          />

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                Chamber
              </label>
              <select
                value={filters.chamber}
                onChange={(e) => setFilters(prev => ({ ...prev, chamber: e.target.value }))}
                className="input"
              >
                <option value="All chambers">All chambers</option>
                <option value="House">House</option>
                <option value="Senate">Senate</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                Subject
              </label>
              <MultiSelectDropdown
                ariaLabel="Subject filter"
                options={filterOptions.subjects.map(s => ({ value: s, label: s }))}
                selected={filters.subject}
                onChange={(next) => setFilters(prev => ({ ...prev, subject: next }))}
                allLabel="All subjects"
                selectedWord="subjects"
                searchable={filterOptions.subjects.length > SEARCHABLE_OPTION_THRESHOLD}
                searchPlaceholder="Search subjects…"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                Status
              </label>
              <MultiSelectDropdown
                ariaLabel="Status filter"
                options={BILL_STATUS_OPTIONS}
                selected={filters.status}
                onChange={(next) => setFilters(prev => ({ ...prev, status: next }))}
                allLabel="All statuses"
                selectedWord="statuses"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                Sponsor
              </label>
              <MultiSelectDropdown
                ariaLabel="Sponsor filter"
                options={filterOptions.sponsors.map(s => ({ value: s, label: s }))}
                selected={filters.sponsor}
                onChange={(next) => setFilters(prev => ({ ...prev, sponsor: next }))}
                allLabel="All sponsors"
                selectedWord="sponsors"
                searchable={filterOptions.sponsors.length > SEARCHABLE_OPTION_THRESHOLD}
                searchPlaceholder="Search sponsors…"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                Committee
              </label>
              <MultiSelectDropdown
                ariaLabel="Committee filter"
                options={getFilteredCommittees(filterOptions.committees, filters.chamber).map(
                  committee => ({
                    value: String(committee.id),
                    label: formatCommitteeDisplayName(committee),
                  })
                )}
                selected={filters.committee_id}
                onChange={(next) =>
                  // Picking from the dropdown supersedes any legacy name-based filter.
                  setFilters(prev => ({ ...prev, committee_id: next, committee: '' }))
                }
                allLabel="All committees"
                selectedWord="committees"
                searchable={
                  getFilteredCommittees(filterOptions.committees, filters.chamber).length >
                  SEARCHABLE_OPTION_THRESHOLD
                }
                searchPlaceholder="Search committees…"
              />
            </div>
          </div>

          <FilterActions
            onClear={handleClearFilters}
            hasFilters={Boolean(searchQuery) || Object.values(filters).some(f => f && f !== 'All chambers' && f !== 'All subjects')}
          />
        </div>
      </div>

      {/* View Toggle and Results Count */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
        
        <div className="flex items-center gap-4">
          {loading && (
            <div className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          )}
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            {totalBillsCount} bills found
          </p>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <>
          {viewMode === 'card' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bills && bills.map(renderCard)}
            </div>
          ) : (
            renderTable()
          )}
          
          {/* Load More button for infinite scroll fallback */}
          {hasMore && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => loadMoreBills()}
                disabled={loadingMore}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading more bills...
                  </>
                ) : (
                  `Load More Bills (${totalBillsCount - bills.length} remaining)`
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Scroll to top button */}
      {showScrollToTop && (
        <>
          {/* Mobile: Above bottom navigation */}
                <button
            onClick={scrollToTop}
            className="lg:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-4 h-4" />
                </button>
          
          {/* Desktop: Bottom right corner */}
                <button
            onClick={scrollToTop}
            className="hidden lg:block fixed bottom-6 right-6 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-5 h-5" />
                </button>
        </>
      )}
    </div>
  );
};

export default BillsTab;